import { guestLikeRoles, userRoles, userTypes } from 'utils/constants';

// A person can be a team member of an org (a record in `orgs`) and a resident or tenant on a
// property (a record in `properties`). Which of the two apps they are in is not decided by the
// root `role` / `type` of the user document, which only reflects one of them, but by what is
// active: the property when it is a resident one, the org otherwise.

export const APP_CONTEXT = {
    RESI: 'resi',
    OPERATOR: 'operator'
};

const SUPERVISOR_TYPE = 'supervisor';

const isLive = record => Boolean(record) && !record.suspended;

const isResiPropertyRole = role =>
    role === userRoles.RESIDENT || role === userRoles.TENANT;

const entriesOf = map =>
    map && typeof map === 'object'
        ? Object.entries(map).map(([key, record]) => [key, record ?? {}])
        : [];

const liveOperatorOrgIds = userData =>
    entriesOf(userData?.orgs)
        .filter(([, record]) => isLive(record))
        .map(([orgId]) => orgId);

const liveResiProperties = userData =>
    entriesOf(userData?.properties)
        .filter(([, record]) => isLive(record) && isResiPropertyRole(record.role))
        .map(([key, record]) => ({ ...record, id: record.id ?? key }));

// The app the user is in now. Super admins and external virtual guards have no resident side
// and stay in the operator app.
export const getAppContext = userData => {
    if (!userData) return null;

    if (userData.type === userTypes.SUPER_ADMIN || userData.external === true) {
        return APP_CONTEXT.OPERATOR;
    }

    const orgRecord = userData.orgs?.[userData.active_org_id];
    const property = userData.properties?.[userData.active_property_id];

    // The active org is one they work in, and the active property is not a resident one in it
    // (an operator property, none at all, or one of another org).
    if (
        orgRecord &&
        (!property ||
            property.role === userRoles.OPERATOR ||
            property.org_id !== userData.active_org_id)
    ) {
        return APP_CONTEXT.OPERATOR;
    }

    if (property && (isResiPropertyRole(property.role) || guestLikeRoles.includes(property.role))) {
        return APP_CONTEXT.RESI;
    }

    // Nothing to derive it from (older records): the root role decides, as it always did.
    return isResiPropertyRole(userData.role) || guestLikeRoles.includes(userData.role)
        ? APP_CONTEXT.RESI
        : APP_CONTEXT.OPERATOR;
};

// Where switching to `context` takes the user: the active org and property to set, or null when
// there is nothing to switch to. A resident side is the first live resident or tenant property.
// The operator side is an org they work in (the active one when it is, else the first) with its
// operator property when there is one. Without an operator property, the operator app can only
// be told apart from the resident one when the resident property belongs to another org.
// `orgId` asks for the operator side of one specific org (a notification's) instead: null when
// they do not work in it.
export const getSwitchTarget = (userData, context, { orgId: requestedOrgId } = {}) => {
    if (!userData || userData.type === userTypes.SUPER_ADMIN || userData.external === true) {
        return null;
    }

    if (context === APP_CONTEXT.RESI) {
        const [property] = liveResiProperties(userData);

        return property
            ? { context, orgId: property.org_id, propertyId: property.id }
            : null;
    }

    if (context === APP_CONTEXT.OPERATOR) {
        const orgIds = liveOperatorOrgIds(userData);

        if (orgIds.length === 0) return null;

        if (requestedOrgId && !orgIds.includes(requestedOrgId)) return null;

        const orgId =
            requestedOrgId ??
            (orgIds.includes(userData.active_org_id)
                ? userData.active_org_id
                : orgIds[0]);
        const operatorProperty = entriesOf(userData.properties)
            .map(([key, record]) => ({ ...record, id: record.id ?? key }))
            .find(
                record =>
                    isLive(record) &&
                    record.role === userRoles.OPERATOR &&
                    record.org_id === orgId
            );

        if (!operatorProperty) {
            const activeProperty = userData.properties?.[userData.active_property_id];

            if (activeProperty?.org_id === orgId) return null;
        }

        return { context, orgId, propertyId: operatorProperty?.id ?? null };
    }

    return null;
};

// Whether they have a live record in `orgs` for this org, i.e. work in it.
export const canWorkInOrg = (userData, orgId) =>
    Boolean(orgId) && liveOperatorOrgIds(userData).includes(orgId);

// Both sides exist: a live record in `orgs` and a live resident or tenant property, and each
// can be switched to.
export const canSwitchApp = userData =>
    Boolean(
        getSwitchTarget(userData, APP_CONTEXT.RESI) &&
            getSwitchTarget(userData, APP_CONTEXT.OPERATOR)
    );

// The user document as the app should read it. For someone with both sides, the root `role`
// and `type` are replaced by those of the active side, so everything that decides on them (route
// guards, permissions, menus, which listeners start) follows the app they are in. Everyone else
// is returned untouched.
export const withActiveContext = userData => {
    if (!userData || !canSwitchApp(userData)) return userData;

    if (getAppContext(userData) === APP_CONTEXT.OPERATOR) {
        const orgType = userData.orgs?.[userData.active_org_id]?.type;

        return {
            ...userData,
            role: userRoles.OPERATOR,
            type: orgType === SUPERVISOR_TYPE ? userTypes.MANAGER : orgType || userData.type
        };
    }

    const property = userData.properties?.[userData.active_property_id];

    return property?.role
        ? { ...userData, role: property.role, type: property.type || userData.type }
        : userData;
};
